import { AlertTriangle, Bell01, InfoCircle, XCircle } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Popover as AriaPopover,
} from "react-aria-components";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/base/buttons/button";
import { formatDateTime } from "@/lib/format";
import {
  notificationMessageKey,
  notificationTitleKey,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationItem,
  type NotificationSeverity,
} from "@/lib/notifications";
import { cx } from "@/utils/cx";

/**
 * Zvoneček v horní liště — posledních 10 oznámení, odznak s počtem nepřečtených.
 *
 * Nepřečtené se hlásí **dvěma způsoby**: rozsvícenou ikonou (šedá ikona v řadě dalších
 * šedých ikon se přehlédne) a odznakem s počtem. Nad 9 se počet zkracuje na `9+` —
 * dvojciferné číslo se do kolečka u ikony nevejde čitelně a přesná hodnota tam nikoho
 * nezajímá, na to je stránka se seznamem.
 *
 * Obsah popoveru **musí být `Dialog`**, ne jen `div`: bez něj react-aria nechá focus
 * management na popoveru a klik na odkaz dovnitř nedojde — přesně tak přestalo fungovat
 * „Zobrazit všechna oznámení". Karta účtu to má stejně (`NavAccountMenu` je `Dialog`).
 *
 * Text se skládá tady z `type` + `params` (viz `lib/notifications.ts`), ne ze serverové
 * věty; kdyby přišel typ bez překladu, spadne se na anglický `title` ze serveru.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const { data } = useNotifications({ size: 10 });
  const markAllRead = useMarkAllNotificationsRead();
  const unread = data?.unreadCount ?? 0;
  const hasUnread = unread > 0;

  return (
    <AriaDialogTrigger>
      <AriaButton
        aria-label={hasUnread ? t("notifications.unreadCount", { count: unread }) : t("header.notifications")}
        className={cx(
          "relative flex cursor-pointer items-center justify-center rounded-md p-2 outline-focus-ring transition",
          "hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2",
          hasUnread ? "text-fg-brand-primary" : "text-fg-quaternary hover:text-fg-quaternary_hover",
        )}
      >
        <Bell01 className="size-5 shrink-0" aria-hidden="true" />
        {hasUnread && (
          <span
            aria-hidden="true"
            className={cx(
              "absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full px-1",
              "bg-fg-error-primary text-[10px] leading-4 font-bold text-white",
              // Prstenec v barvě lišty odděluje odznak od ikony pod ním.
              "ring-2 ring-bg-primary",
            )}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </AriaButton>

      <AriaPopover placement="bottom right" offset={8}>
        <AriaDialog
          aria-label={t("notifications.title")}
          className="flex w-96 max-w-[calc(100vw-2rem)] flex-col rounded-xl bg-primary shadow-lg ring ring-secondary outline-hidden"
        >
          {({ close }) => (
            <>
              <div className="flex items-center justify-between border-b border-secondary px-4 py-3">
                <p className="text-sm font-semibold text-primary">
                  {t("notifications.title")}
                  {hasUnread && <span className="ml-1.5 font-normal text-tertiary">({unread})</span>}
                </p>
                {hasUnread && (
                  <Button size="sm" color="link-gray" onPress={() => markAllRead.mutate()} isLoading={markAllRead.isPending}>
                    {t("notifications.markAllRead")}
                  </Button>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto p-2">
                {data && data.items.length === 0 && (
                  <p className="px-2 py-6 text-center text-sm text-tertiary">{t("notifications.empty")}</p>
                )}
                {data?.items.map((item) => <NotificationRow key={item.id} item={item} onNavigate={close} />)}
              </div>

              <Link
                to="/settings/notifications"
                onClick={close}
                className="border-t border-secondary px-4 py-2.5 text-center text-sm font-medium text-brand-secondary hover:bg-primary_hover hover:text-brand-secondary_hover"
              >
                {t("notifications.viewAll")}
              </Link>
            </>
          )}
        </AriaDialog>
      </AriaPopover>
    </AriaDialogTrigger>
  );
}

const SEVERITY_ICON: Record<NotificationSeverity, typeof InfoCircle> = {
  INFO: InfoCircle,
  WARNING: AlertTriangle,
  ERROR: XCircle,
};

const SEVERITY_TONE: Record<NotificationSeverity, string> = {
  INFO: "text-fg-quaternary",
  WARNING: "text-fg-warning-primary",
  ERROR: "text-fg-error-primary",
};

function NotificationRow({ item, onNavigate }: { item: NotificationItem; onNavigate: () => void }) {
  const { t } = useTranslation();
  const markRead = useMarkNotificationRead();
  const Icon = SEVERITY_ICON[item.severity] ?? InfoCircle;

  // `defaultValue` = anglický titulek ze serveru: neznámý typ (starší řádek v DB, nový
  // typ bez překladu) se pořád zobrazí, jen bez lokalizace.
  const title = t(notificationTitleKey(item.type), { ...item.params, defaultValue: item.title });
  const message = t(notificationMessageKey(item.type), { ...item.params, defaultValue: item.message ?? "" });

  const markIfUnread = () => {
    if (item.readAt == null) {
      markRead.mutate(item.id);
    }
  };

  const body = (
    <>
      <Icon className={cx("mt-0.5 size-4 shrink-0", SEVERITY_TONE[item.severity])} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium text-primary">{title}</span>
          {item.occurrences > 1 && (
            <span className="shrink-0 rounded-full bg-secondary px-1.5 text-xs text-tertiary">×{item.occurrences}</span>
          )}
        </span>
        {message && <span className="mt-0.5 line-clamp-2 block text-xs text-tertiary">{message}</span>}
        <span className="mt-1 block text-xs text-quaternary">{formatDateTime(item.lastSeenAt)}</span>
      </span>
      {item.readAt == null && (
        <span
          aria-label={t("notifications.unread")}
          className="mt-1.5 size-2 shrink-0 rounded-full bg-fg-brand-primary"
        />
      )}
    </>
  );

  const className = cx(
    "flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-primary_hover",
    item.readAt == null && "bg-active",
  );

  // Odkaz jen když je kam jít; jinak by to bylo tlačítko maskované za odkaz a rozbilo
  // by „otevřít v novém panelu". `Link` řeší basepath `/app` i klik routerem.
  return item.link ? (
    <Link
      to={item.link}
      className={className}
      onClick={() => {
        markIfUnread();
        onNavigate();
      }}
    >
      {body}
    </Link>
  ) : (
    <button type="button" className={className} onClick={markIfUnread}>
      {body}
    </button>
  );
}
